import type { TargetAndTransition, Transition } from "motion/react";
import { UI_MOTION } from "../../../config/uiMotion.configuration";
import { resolveUiEaseOut, resolveUiSpring } from "../../shared/motion/uiMotion.tools";

/** Tailwind spacing class replacing parent `gap-2` between saved list rows. */
export const SAVED_ITINERARY_LIST_ITEM_SPACING_CLASS_NAME = "mb-2 shrink-0";

/** Pixel margin matching `mb-2` for animated open/close spacing. */
const SAVED_LIST_ITEM_MARGIN_BOTTOM_PX = 8;

/** Animation profile for a saved-itinerary list row. */
export type SavedItineraryListItemAnimationProfile = "default" | "temporal";

/**
 * Computes stagger delay for rows revealed when a saved-list accordion section opens.
 *
 * @param index Zero-based row index within the expanded section.
 * @param reducedMotion Whether the user prefers reduced motion.
 * @returns Delay in seconds for Motion `transition.delay`.
 */
const resolveSavedListTemporalStagger = ( index: number, reducedMotion: boolean ): number => {
    if ( reducedMotion || !UI_MOTION.enabled ) {
        return 0;
    }

    return ( index * UI_MOTION.savedListTemporalStaggerMs ) / 1000;
};

/**
 * Initial motion state for a default saved-itinerary list row reveal.
 *
 * @returns Motion initial target with collapsed height so open matches the paced exit.
 */
export const buildSavedItineraryListItemInitialState = (): TargetAndTransition => {
    return {
        "opacity":      0,
        "y":            10,
        "height":       0,
        "marginBottom": 0,
    };
};

/**
 * Initial motion state for a temporal (accordion) row reveal.
 *
 * @returns Motion initial target collapsing from above with height 0.
 */
export const buildSavedItineraryListItemTemporalInitialState = (): TargetAndTransition => {
    return {
        "opacity":      0,
        "y":            -10,
        "height":       0,
        "marginBottom": 0,
    };
};

/**
 * Motion target for a revealed saved-itinerary list row.
 *
 * @returns Motion animate target including measured height restoration.
 */
export const buildSavedItineraryListItemVisibleState = (): TargetAndTransition => {
    return {
        "opacity":      1,
        "y":            0,
        "height":       "auto",
        "marginBottom": SAVED_LIST_ITEM_MARGIN_BOTTOM_PX,
    };
};

/**
 * Layout transition used when siblings reflow after section expand/collapse.
 *
 * @param reducedMotion Whether the user prefers reduced motion.
 * @returns Motion layout transition.
 */
export const buildSavedItineraryListItemLayoutTransition = ( reducedMotion: boolean ): Transition => {
    return resolveUiSpring( reducedMotion, "sheet" );
};

/**
 * Staggered enter transition for a saved-itinerary list row.
 *
 * @param index Zero-based row index within the section being revealed.
 * @param reducedMotion Whether the user prefers reduced motion.
 * @param profile Row animation profile.
 * @returns Motion transition for the row reveal (height + fade + slide).
 */
export const buildSavedItineraryListItemEnterTransition = (
    index: number,
    reducedMotion: boolean,
    profile: SavedItineraryListItemAnimationProfile = "default",
): Transition => {
    const durationMs = profile === "temporal"
        ? UI_MOTION.savedListTemporalEnterMs
        : UI_MOTION.duration.sheetMs;
    const enterTransition = resolveUiEaseOut( reducedMotion, durationMs );

    return {
        "height":       enterTransition,
        "marginBottom": enterTransition,
        "opacity":      enterTransition,
        "y":            enterTransition,
        "delay":        resolveSavedListTemporalStagger( index, reducedMotion ),
    };
};

/**
 * Exit motion state for a removed saved-itinerary list row.
 *
 * @returns Motion exit target with height collapse for sibling reflow.
 */
export const buildSavedItineraryListItemExitState = (): TargetAndTransition => {
    return {
        "height":       0,
        "marginBottom": 0,
        "opacity":      0,
    };
};

/**
 * Ease-out transition for saved-itinerary list row removal.
 *
 * @param reducedMotion Whether the user prefers reduced motion.
 * @param profile Row animation profile.
 * @returns Motion transition for height collapse and fade-out.
 */
export const buildSavedItineraryListItemExitTransition = (
    reducedMotion: boolean,
    profile: SavedItineraryListItemAnimationProfile = "default",
): Transition => {
    const durationMs = profile === "temporal"
        ? UI_MOTION.savedListTemporalExitMs
        : UI_MOTION.duration.fastMs;
    const exitTransition = resolveUiEaseOut( reducedMotion, durationMs );

    return {
        "height":       exitTransition,
        "marginBottom": exitTransition,
        "opacity":      exitTransition,
    };
};

/**
 * Enter transition for the saved-itinerary empty state panel.
 *
 * @param reducedMotion Whether the user prefers reduced motion.
 * @returns Motion transition for empty-state fade-in.
 */
export const buildSavedItineraryEmptyStateEnterTransition = ( reducedMotion: boolean ): Transition => {
    return resolveUiEaseOut( reducedMotion, UI_MOTION.duration.fastMs );
};

/**
 * Resolves the animation profile for a saved-itinerary list row.
 *
 * @remarks Accordion open/close always uses the paced temporal profile so enter grows
 * height over time instead of springing in instantly while exit already collapses.
 *
 * @returns Animation profile for the row shell.
 */
export const resolveSavedItineraryListItemAnimationProfile = (): SavedItineraryListItemAnimationProfile => {
    return "temporal";
};
